// One search box for every kind of holding: the code (or name) decides what it is and where it trades, so the
// user never has to pick "ETF vs mutual fund" or "US vs Canada" first. Sources: exchange listings (US needs a
// quote key; Canada is public), TMX Money for FundSERV fund codes, and the fixed coin / currency lists.
import { t } from '@/i18n';
import { hasQuoteSource, settingsStore } from '@/settings/settingsStore';
import { CRYPTO_SYMBOLS, DISPLAY_CURRENCIES, looksLikeFundCode, type Market } from '@/types';
import { relevance, searchSymbols, type SearchHit } from './search';
import { tmxFund, tmxLookup, type TmxFund } from './tmx';

export type Candidate =
  | { kind: 'stock' | 'etf'; symbol: string; name: string; market: Market; mic?: string; exchange?: string; listingType: string; currency: string }
  | { kind: 'fund'; symbol: string; name: string; market: 'CA'; currency: 'CAD'; fund?: TmxFund } // `fund` when TMX Money knows the code
  | { kind: 'crypto'; symbol: string; name: string; currency: 'USD' }
  | { kind: 'cash'; symbol: string; name: string; currency: string };

export const CRYPTO_NAMES: Record<string, string> = { BTC: 'Bitcoin', ETH: 'Ethereum', USDC: 'USD Coin', USDT: 'Tether' };

const MAX = 10;

export function listingCandidate(hit: SearchHit, market: Market): Candidate {
  return { kind: hit.assetClass, symbol: hit.symbol.toUpperCase(), name: hit.name, market, mic: hit.mic, exchange: hit.exchange, listingType: hit.type, currency: (hit.currency ?? (market === 'CA' ? 'CAD' : 'USD')).toUpperCase() };
}

export function cryptoCandidate(symbol: string): Candidate {
  return { kind: 'crypto', symbol, name: CRYPTO_NAMES[symbol] ?? symbol, currency: 'USD' };
}

export function cashCandidate(ccy: string): Candidate {
  return { kind: 'cash', symbol: ccy, name: t('list.cashName', { ccy }), currency: ccy };
}

/** What to offer before anything is typed: the holdings that have no code to search for. */
export function quickCandidates(): Candidate[] {
  return [...DISPLAY_CURRENCIES.map(cashCandidate), ...CRYPTO_SYMBOLS.map(cryptoCandidate)];
}

/**
 * Pure; exported for tests. Merges what every source returned into one ranked list: an exact code first, the
 * US listing ahead of a Canadian one with the same ticker (a CDR), then prefix and name matches.
 */
export function mergeCandidates(query: string, found: { fund?: TmxFund | null; us?: SearchHit[]; ca?: SearchHit[] }): Candidate[] {
  const q = query.trim().toUpperCase();
  if (!q) return [];
  const scored: { c: Candidate; score: number; order: number }[] = [];
  if (found.fund) scored.push({ c: { kind: 'fund', symbol: q, name: found.fund.name, market: 'CA', currency: 'CAD', fund: found.fund }, score: 0, order: 0 });
  for (const h of found.us ?? []) scored.push({ c: listingCandidate(h, 'US'), score: relevance(h, q), order: 1 });
  for (const h of found.ca ?? []) scored.push({ c: listingCandidate(h, 'CA'), score: relevance(h, q), order: 2 });
  for (const s of CRYPTO_SYMBOLS) {
    const name = CRYPTO_NAMES[s].toUpperCase();
    const score = s === q ? 0 : s.startsWith(q) ? 2 : name.startsWith(q) ? 3 : name.includes(q) ? 4 : -1;
    if (score >= 0) scored.push({ c: cryptoCandidate(s), score, order: 4 });
  }
  const cashWord = /^(CASH|现金|CURRENCY|货币)$/.test(q);
  for (const ccy of DISPLAY_CURRENCIES) {
    const name = t('list.cashName', { ccy }).toUpperCase();
    const score = ccy === q ? 0 : cashWord ? 3 : ccy.startsWith(q) ? 2 : name.includes(q) ? 4 : -1;
    if (score >= 0) scored.push({ c: cashCandidate(ccy), score, order: 3 });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.order - b.order)
    .slice(0, MAX)
    .map((x) => x.c);
}

const TICKER = /^[A-Z][A-Z0-9.]{0,7}$/;

/** Without a US key there is no name search, but TMX Money still answers an exact ticker (delayed quotes). */
async function usByTicker(q: string, signal?: AbortSignal): Promise<SearchHit[]> {
  const code = q.toUpperCase();
  if (!TICKER.test(code)) return [];
  const hit = await tmxLookup(`${code}:US`, signal);
  if (!hit || !/^(equity|etf)$/i.test(hit.datatype)) return [];
  const etf = /etf/i.test(hit.datatype);
  return [{ symbol: code, name: hit.name, type: etf ? 'ETF' : 'Common Stock', assetClass: etf ? 'etf' : 'stock', exchange: hit.exchangeName, market: 'US', currency: 'USD' }];
}

/** Asks every source that applies, in parallel; a source that fails just contributes nothing. */
export async function resolveQuery(query: string, signal?: AbortSignal): Promise<{ candidates: Candidate[]; usSearched: boolean }> {
  const q = query.trim();
  if (!q) return { candidates: [], usSearched: false };
  const usSearched = hasQuoteSource(settingsStore.get());
  const [fund, us, ca] = await Promise.allSettled([
    looksLikeFundCode(q) ? tmxFund(q, signal) : Promise.resolve(null),
    usSearched ? searchSymbols(q, 'etf', 'US', signal) : usByTicker(q, signal),
    searchSymbols(q, 'etf', 'CA', signal),
  ]);
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
  const value = <T,>(r: PromiseSettledResult<T>, fallback: T): T => (r.status === 'fulfilled' ? r.value : fallback);
  return { candidates: mergeCandidates(q, { fund: value(fund, null), us: value(us, []), ca: value(ca, []) }), usSearched };
}
